import { Route, Switch, Redirect } from 'wouter';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useEffect, useState } from 'react';
import useAuthState from '../../common/src/hooks/useAuthState';
import useSpeciesTheme from '../../common/src/hooks/useSpeciesTheme';
import { supabase } from '../../common/src/supabase';
import SidebarLayout from './components/layouts/SidebarLayout';
import AuthPage from './pages/auth/AuthPage';
import MarketTodayPage from './pages/market/MarketTodayPage';
import MarketEvolutionPage from './pages/market/MarketEvolutionPage';
import ProfileSelectionPage from './pages/ProfileSelectionPage';
import ReceiverOverviewPage from './pages/receiver/ReceiverOverviewPage';
import ReceiverShipmentsPage from './pages/receiver/ReceiverShipmentsPage';
import ReceiverCommitmentsPage from './pages/receiver/ReceiverCommitmentsPage';
import {
  ExternalProfile,
  ExternalReceiverProfile,
  getExternalProfiles,
} from '../../common/src/api/external-profiles';
import { Spinner } from '@nextui-org/react';

function App() {
  const { session, loading } = useAuthState();
  const [profiles, setProfiles] = useState<ExternalProfile[]>([]);
  const [selectedProfile, setSelectedProfile] =
    useState<ExternalProfile | null>(null);
  const [profilesLoading, setProfilesLoading] = useState(false);
  // Keep --brand-* CSS variables in sync with selectedSpecies + darkMode.
  useSpeciesTheme();

  useEffect(() => {
    if (!session) {
      setProfiles([]);
      setSelectedProfile(null);
      return;
    }

    let cancelled = false;
    const loadProfiles = async () => {
      setProfilesLoading(true);
      try {
        const payload = await getExternalProfiles();
        if (cancelled) return;
        setProfiles(payload.profiles);
        if (payload.profiles.length === 1) {
          setSelectedProfile(payload.profiles[0]);
        } else {
          setSelectedProfile(null);
        }
        if (payload.profiles.length === 0) {
          await supabase.auth.signOut();
        }
      } catch (error) {
        console.error('Error loading external profiles:', error);
        if (!cancelled) {
          await supabase.auth.signOut();
        }
      } finally {
        if (!cancelled) {
          setProfilesLoading(false);
        }
      }
    };

    loadProfiles();
    return () => {
      cancelled = true;
    };
  }, [session?.access_token]);

  if (loading) {
    return (
      <div className='min-h-screen flex items-center justify-center bg-default-50'>
        <Spinner size='lg' />
      </div>
    );
  }

  if (!session) {
    return (
      <>
        <AuthPage />
        <ToastContainer
          position='top-right'
          autoClose={3000}
          hideProgressBar={false}
        />
      </>
    );
  }

  if (session && (profilesLoading || profiles.length === 0)) {
    return (
      <div className='min-h-screen flex items-center justify-center bg-default-50'>
        <Spinner size='lg' />
      </div>
    );
  }

  if (!selectedProfile && profiles.length > 1) {
    return (
      <>
        <ProfileSelectionPage
          profiles={profiles}
          onSelect={setSelectedProfile}
        />
        <ToastContainer
          position='top-right'
          autoClose={3000}
          hideProgressBar={false}
        />
      </>
    );
  }

  const activeProfile = selectedProfile || profiles[0];
  const receiverProfile =
    activeProfile?.type === 'receiver'
      ? (activeProfile as ExternalReceiverProfile)
      : null;

  return (
    <>
      <SidebarLayout
        selectedProfile={activeProfile}
        canSwitchProfile={profiles.length > 1}
        onSwitchProfile={() => setSelectedProfile(null)}
      >
        {receiverProfile ? (
          <Switch>
            <Route
              path='/'
              component={() => <Redirect to='/receiver-overview' />}
            />
            <Route
              path='/receiver-overview'
              component={() => <ReceiverOverviewPage profile={receiverProfile} />}
            />
            <Route
              path='/receiver-shipments'
              component={() => <Redirect to='/receiver-shipments/summary' />}
            />
            <Route
              path='/receiver-shipments/summary'
              component={() => (
                <ReceiverShipmentsPage
                  profile={receiverProfile}
                  view='summary'
                />
              )}
            />
            <Route
              path='/receiver-shipments/detail'
              component={() => (
                <ReceiverShipmentsPage profile={receiverProfile} view='detail' />
              )}
            />
            <Route
              path='/receiver-commitments'
              component={() => <Redirect to='/receiver-commitments/summary' />}
            />
            <Route
              path='/receiver-commitments/summary'
              component={() => (
                <ReceiverCommitmentsPage
                  profile={receiverProfile}
                  view='summary'
                />
              )}
            />
            <Route
              path='/receiver-commitments/detail'
              component={() => (
                <ReceiverCommitmentsPage
                  profile={receiverProfile}
                  view='detail'
                />
              )}
            />
            <Route>
              <Redirect to='/receiver-overview' />
            </Route>
          </Switch>
        ) : (
          <Switch>
            <Route path='/' component={() => <Redirect to='/market-today' />} />
            <Route path='/market-today' component={MarketTodayPage} />
            <Route path='/market-evolution' component={MarketEvolutionPage} />
            <Route>
              <Redirect to='/market-today' />
            </Route>
          </Switch>
        )}
      </SidebarLayout>
      <ToastContainer
        position='top-right'
        autoClose={3000}
        hideProgressBar={false}
      />
    </>
  );
}

export default App;
